import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { TerminalClause } from '@atlaskit/jql-ast';
import type {
	SearchTemplate,
	SelectOption,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { hydrateJqlBuilderQuery$data } from '@atlassian/jira-relay/src/__generated__/hydrateJqlBuilderQuery.graphql';
import type { ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data } from '@atlassian/jira-relay/src/__generated__/ui_jqlBuilder_JQLBuilderUI_hydrationQueryData.graphql';
import { OperandValues } from '../../../../../common/constants.tsx';
import { useHydratedValues } from '../use-hydrated-values/index.tsx';

const isEmptyOption = (option: SelectOption) =>
	option.optionType === 'option' && option.value === OperandValues.empty.value;

export const useSelectedValues = (
	terminalClause: TerminalClause | undefined,
	hydratedData: hydrateJqlBuilderQuery$data | ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data,
	fieldId: string,
	operandDisplayValues: string[] | undefined,
	searchTemplate: SearchTemplate,
) => {
	// Allow optimistic updates when a value is selected while hydration is taking place
	const [optimisticValues, setOptimisticValues] = useState<SelectValue>([]);
	const [hydrated, setHydrated] = useState(false);
	const prevHydratedData = useRef<
		hydrateJqlBuilderQuery$data | ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data
	>();

	const hydratedValues = useHydratedValues(
		fieldId,
		hydratedData,
		searchTemplate,
		terminalClause,
		operandDisplayValues,
	);

	// Only flag values as hydrated after a data change
	useEffect(() => {
		if (!hydrated && hydratedData !== prevHydratedData.current) {
			setHydrated(true);
		}
		prevHydratedData.current = hydratedData;
	}, [hydrated, hydratedData]);

	const selectedValues = useMemo(() => {
		const values = hydrated ? hydratedValues : optimisticValues;
		const emptyIndex = values.findIndex(isEmptyOption);
		if (emptyIndex !== -1) {
			const orderedValues = [...values];
			// Rearrange selected values to keep EMPTY at the end
			orderedValues.push(orderedValues.splice(emptyIndex, 1)[0]);
			return orderedValues;
		}
		return values;
	}, [hydrated, hydratedValues, optimisticValues]);

	const setSelectedValues = useCallback((values: SelectValue) => {
		setHydrated(false);
		setOptimisticValues(values);
	}, []);

	return [selectedValues, setSelectedValues] as const;
};
